<template>
  <div>
    <v-row>
      <v-col>
        <v-app
          v-cloak
          id="dayspan"
        >
          <dsCalendarApp
            ref="appointmentPage"
            :calendar="calendar"
            :readOnly="readOnly"
            :isSite="site.id && !!site.id.length"
            @event-create="saveState"
            @event-update="update"
            @event-remove="remove"
            @calendar-state-changed="calendarStateChanged"
          >
            <template slot="title">
              {{ $t('appointment.title') }}
            </template>

            <!-- popup when event is clicked -->
            <template
              slot="eventPopover"
              slot-scope="slotData"
            >
              <ds-calendar-event-popover
                v-bind="slotData"
                :readOnly="readOnly"
                @event-remove="remove"
                @event-update="update"
                @start="start"
              />
            </template>
            <!-- Create popup -->
            <template
              slot="eventCreatePopover"
              slot-scope="{placeholder, calendar}"
            >
              <ds-calendar-event-create-popover
                :calendarEvent="placeholder"
                class="blocAppointment"
                :calendar="calendar"
                :close="$refs.appointmentPage.$refs.calendar.clearPlaceholder"
                @create-edit="$refs.appointmentPage.editPlaceholder"
                @event-create="saveState"
              />
              <template
                name="eventCreatePopoverBodyBottom"
              >
                <AppointmentFields
                  :model="appointmentInfos"
                  :appointments="appointments"
                  :lang="lang"
                  :sites="sites"
                  :client="site.client"
                  :whichCalendar="whichCalendar"
                  :members="members"
                  @fetch-next-page="getSites"
                />
              </template>
            </template>

            <!-- Edit popup -->
            <template
              slot="eventDetailsExtra"
              slot-scope="{details}"
            >
              <div class="fields">
                <AppointmentFields
                  :model="!details.assignedTo ? appointmentInfos : details"
                  :appointments="appointments"
                  :lang="lang"
                  :sites="sites"
                  :client="site.client"
                  :members="members"
                  :whichCalendar="whichCalendar"
                  @fetch-next-page="getSites"
                />
              </div>
            </template>
            <!-- Edit minute popup -->
            <template
              slot="eventTimeTitle"
              slot-scope="{calendarEvent, details}"
            >
              <div>
                <v-icon
                  v-if="details.icon"
                  class="ds-ev-icon"
                  size="14"
                  :style="{color: details.forecolor}"
                >
                  {{ details.icon }}
                </v-icon>
                <strong class="ds-ev-title">{{ details.title }}</strong>
              </div>
              <div class="ds-ev-description">
                {{ getCalendarTime( calendarEvent ) }}
              </div>
            </template>
          </dsCalendarApp>
        </v-app>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { Calendar } from 'dayspan';
import Vue from 'vue';
import { mapGetters, mapActions } from 'vuex';
import fr from 'dayspan-vuetify-2/src/locales/fr';
import WorkOrderWorkOrderType from '@/components/forms/workOrder/WorkOrderTypesForm';
import ReportTypeForm from '@/components/forms/workOrder/ReportTypeForm';
import workOrderTypes from '@/utilities/enums/workOrderTypes';
import completionStatus from '@/constants/workOrderStatusChoices';
import roles from '@/constants/roles';
import { isEmpty, cloneDeep } from 'lodash';
import AppointmentFields from './AppointmentFields.vue';
import AppointmentError from './Error.vue';

const defaultPopulateKeys = ['durationUnit', 'durationInDays', 'duration'];

export default {
    components: {
        AppointmentFields,
    },

    data: () => ({
        calendar: Calendar.months(),
        events: [],
        readOnly: false,
        appointments: [],
        appointmentInfos: {
            po: '',
            so: '',
            workOrderType: null,
            reportType: null,
            billable: false,
            priority: 1,
            description: '',
            site: null,
            limitDate: null,
            limiteForCompletingTheJob: null,
            menu: null,
            time: '',
            assignedTo: [],
        },
        sites: [],
        pagination: {
            totalElementCount: 1,
        },
        member: null,
        members: [],
        prioritiesValues: ['low', 'normal', 'high'],
    }),

    computed: {
        ...mapActions({
            setLang: 'setLang',
        }),
        ...mapGetters([
            'lang',
            'hasPermission',
            'userLang',
            'teamID',
            'hasRole',
            'currentMember',
            'site',
        ]),
        whichCalendar() {
            return isEmpty(this.site) ? 'personnalCalendar' : 'siteCalendar';
        },
    },
    watch: {
        lang() {
            this.applyCalendarSettings();
            this.getAppointments();
        },
    },
    async mounted() {
        window.appointmentPage = this.$refs.appointmentPage;
        await this.initSiteInfo();
        this.applyCalendarSettings();
        this.loadData();
    },
    methods: {
        async getSites() {
            if (this.sites.length < this.pagination?.totalElementCount) {
                const { data } = await this.$rq.getSites({ page: this.pagination?.currentPage ? this.pagination?.currentPage + 1 : 1 });
                this.sites = [...this.sites, ...data.sites];
                this.pagination = data.pagination;
            }
        },
        async getMemberInfo() {
            const { data } = await this.$rq.getMemberInfo('me');
            this.member = data;
            if (!this.appointmentInfos.assignedTo?.length) {
                // assigned to current user by default
                this.appointmentInfos.assignedTo.push(this.member.id);
            }
            this.getMembers();
        },
        async getAppointments() {
            let data;
            if (this.site.id) {
                ({ data } = await this.$rq.getAppointmentsBySite(this.site.id));
            } else {
                ({ data } = this.isAdminOrManager()
                    ? await this.$rq.getAppointments() : await this.$rq.getMyAppointments());
            }
            this.appointments = data;
        },
        async addAppointment(content) {
            const year = content.calendarInfo.schedule.year[0];
            const month = content.calendarInfo.schedule.month[0];
            const day = content.calendarInfo.schedule.dayOfMonth[0];

            const isoDate = new Date(Date.UTC(year, month, day)).toISOString().split('T')[0];

            const payload = { ...content, dateAppointmentIso: isoDate };
            if (payload.site && typeof payload.site !== 'string') {
                payload.site = payload.site.id;
            }
            try {
                const { data } = await this.$rq.addAppointment(payload);
                return data;
            } catch (e) {
                return e;
            }
        },
        async editAppointment(appointmentId, content) {
            const year = content.calendarInfo.schedule.year[0];
            const month = content.calendarInfo.schedule.month[0];
            const day = content.calendarInfo.schedule.dayOfMonth[0];
            const isoDate = new Date(Date.UTC(year, month, day)).toISOString().split('T')[0];

            const payload = { ...content, dateAppointmentIso: isoDate };

            if (typeof payload.site !== 'string') {
                payload.site = payload.site.id;
            }
            try {
                const { data } = await this.$rq.editAppointment(appointmentId, content);
                return data;
            } catch (e) {
                // eslint-disable-next-line no-console
                console.error(this.$t('appointment.errorEdit'), e);
                return e;
            }
        },
        async deleteAppointment(appointmentId) {
            const { data } = await this.$rq.deleteAppointment(appointmentId);
            return data;
        },
        async loadData() {
            const state = {
                events: [],
            };
            await this.getAppointments();
            if (this.appointments.length) {
                this.appointments.forEach(ev => {
                    const defaults = this.$dayspan.getDefaultEventDetails();
                    // if the current event has color we change the default color.
                    if (ev.calendarInfo.color.length) {
                        defaults.color = ev.calendarInfo.color;
                    }
                    // if the current event has forecolor we change the default color.
                    if (ev.calendarInfo.forecolor.length) {
                        defaults.forecolor = ev.calendarInfo.forecolor;
                    }
                    // set event title.
                    defaults.title = ev.jobName;

                    ev.priority = {
                        id: this.prioritiesValues.indexOf(ev.priority),
                        value: ev.priority,
                    };

                    ev.limitDate = this.formatDate(ev.limiteForCompletingTheJob);

                    // add permission if in site view
                    if (this.site.id) {
                        ev.isSite = true;
                        ev.canEdit = this.isAdminOrManager() ? true : ev.createdBy === this.currentMember.id;
                    }
                    // set data and event duration.
                    const event = {
                        schedule: ev.calendarInfo.schedule,
                        data: Vue.util.extend(defaults, ev),
                    };
                    state.events.push(event);
                });
                this.$refs.appointmentPage.setState(state);
                this.refreshCalendar();
            }
        },
        async initSiteInfo() {
            await this.getMemberInfo();
            if (this.site.id) {
                this.sites = [this.site];
                this.appointmentInfos.site = this.site.id;
            } else {
                this.getSites();
            }
        },
        isAdminOrManager() {
            return this.hasRole('manager') || this.hasRole('administrator');
        },
        applyCalendarSettings() {
            const titleLocation = {
                defaults: {
                    dsCalendarEventCreatePopover: {
                        labels: {
                            title: '',
                        },
                    },
                },
            };

            // check if the key exist for current lang.
            const langLocale = this.$t('appointment.calendar.locales');
            if (typeof langLocale === 'object') {
                this.$dayspan.addLocales(Object.values(langLocale), this.getLanguage());
            }
            titleLocation.defaults.dsCalendarEventCreatePopover.labels.title = this.$t('appointment.jobName');
            this.$dayspan.updateLocale(this.lang, titleLocation);
            this.$dayspan.supports.location = false;
            this.$dayspan.supports.busy = false;
            this.$dayspan.supports.icon = false;
            this.$dayspan.supports.calendar = false;
            this.$dayspan.supports.calendar = false;
            this.$dayspan.supports.description = false;
            // set calendar view
            if (this.member.calendarSettings) {
                this.calendar.set(this.member.calendarSettings);
            }
            this.setCalendarLanguage();
        },
        getLanguage() {
            switch (this.lang) {
                case ('fr'):
                    return fr;
                default:
                    return null;
            }
        },
        populateDataForMobile(inputObject, outputObject, keys = defaultPopulateKeys) {
            // add the 'checks' property
            if (!outputObject.schedule.checks) {
                outputObject.schedule.checks = inputObject.schedule.checks.map(check => ({
                    given: check.given,
                    input: check.input,
                    property: check.property,
                }));
            }
            // add all other wanted properties
            keys.forEach(property => {
                if (!outputObject.schedule[property]) {
                    outputObject.schedule[property] = inputObject.schedule[property];
                }
            });
        },
        async saveState($event) {
            const state = this.calendar.toInput(true);
            const userInput = cloneDeep(state.events[state.events.length - 1]);
            this.populateDataForMobile($event, userInput);
            if (userInput) {
                const request = this.getRequestObject(userInput, this.appointmentInfos);
                const res = await this.addAppointment(request);
                // clean input if success.
                if (res.id) {
                    this.reload();
                }
            }
        },
        async update(payload, skipCreateRequest = false) {
            let res = {};
            if (payload) {
                const request = this.getRequestObject(payload, null, { isEdit: true, skipCreateRequest });
                res = await this.editAppointment(payload.data.id, request);
                // clean input if success.
                if (res.id) {
                    this.reload();
                }
            }
            return res;
        },
        async remove(event) {
            const { data } = event;
            if (data) {
                const res = await this.deleteAppointment(data.id);
                if (res.id) {
                    this.reload();
                }
            }
        },
        start(params) {
            const { details } = params;
            const extraData = {
                workOrderType: null,
                reportType: null,
            };
            if ((details && details.workOrderType && !details.workOrderType.name.length) || !details.workOrderType) {
                this.$root.$emit('showModal', {
                    title: this.$t('workOrders.chooseworkOrderVisitSelection'),
                    buttonTitle: 'start',
                    clean: true,
                    destroy: false,
                    component: Vue.component('WorkOrderWorkOrderType', WorkOrderWorkOrderType),
                    onConfirm: requestPayload => {
                        extraData.workOrderType = requestPayload;
                        this.checkIfReportTypeIsSelected(details, extraData);
                    },
                });
            } else {
                this.checkIfReportTypeIsSelected(details, extraData);
            }
        },
        checkIfReportTypeIsSelected(details, extraData) {
            if ((details && details.reportType && !details.reportType?.length) || !details.reportType) {
                this.$root.$emit('showModal', {
                    title: this.$t('workOrders.choosereportVisitSelection'),
                    buttonTitle: 'start',
                    clean: true,
                    destroy: false,
                    component: Vue.component('ReportTypeForm', ReportTypeForm),
                    onConfirm: requestPayload => {
                        extraData.reportType = requestPayload;
                        this.$root.$emit('destroyModal');
                        this.addWorkOrderTypesToWorkOrder(details, extraData);
                    },
                });
            } else {
                this.$root.$emit('destroyModal');
                this.addWorkOrderTypesToWorkOrder(details, extraData);
            }
        },
        async addWorkOrderTypesToWorkOrder(requestPayload, userNewPayload) {
            this.loading = true;
            const { calendarInfo } = requestPayload;
            const { schedule } = calendarInfo;
            const startDate = new Date(schedule.year[0], schedule.month[0], schedule.dayOfMonth[0]);
            let endDate = new Date(schedule.year[0], schedule.month[0], schedule.dayOfMonth[0]);
            const today = new Date();

            if (schedule.duration) {
                const time = endDate.getTime() + schedule.duration * 60000;
                endDate = new Date(time);
            }
            const isToday = (today.toDateString() === startDate.toDateString());
            if (!isToday) {
                this.$root.$emit('showModal', {
                    title: '',
                    buttonTitle: 'confirm',
                    clean: true,
                    destroy: false,
                    isForm: false,
                    component: Vue.component('AppointmentError', AppointmentError),
                    payload: { date: this.$options.filters.formatDate(startDate) },
                    onConfirm: () => {
                        this.$root.$emit('destroyModal');
                        this.updateAndAddWorkOrder(requestPayload, today, userNewPayload);
                    },
                });
                return;
            }
            let assignedToId = '';
            if (this.appointmentInfos.assignedTo?.length) {
                // eslint-disable-next-line prefer-destructuring
                assignedToId = this.appointmentInfos.assignedTo[0];
            } else {
                // eslint-disable-next-line no-underscore-dangle
                assignedToId = requestPayload.assignedTo[0].id || requestPayload.assignedTo[0]._id;
            }
            const workOrder = {
                site: requestPayload.site.id,
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                type: workOrderTypes.VISIT,
                assignedTo: [assignedToId],
                completionStatus: completionStatus.IN_PROGRESS,
                workOrderTypes: !userNewPayload.workOrderType ? [requestPayload.workOrderType.id]
                    : userNewPayload.workOrderType.selectedWorkOrdertypes,
                billable: requestPayload.billable,
                jobName: requestPayload.jobName,
                priority: requestPayload.priority.value,
                description: requestPayload.description,
                poNumber: requestPayload.po,
                soNumber: requestPayload.so,
                reportType: !userNewPayload.reportType ? requestPayload.reportType : userNewPayload.reportType.selectedReportType,
            };
            const { data } = await this.$rq.addWorkOrder(workOrder);

            await this.$router.push({
                name: 'WorkOrderView',
                params: {
                    workOrderId: data.id,
                    clientId: data.site.client.ref,
                    siteId: data.site.ref,
                },
            });
        },
        async updateAndAddWorkOrder(payload, today, userNewPayload) {
            // update schedule date.
            payload.calendarInfo.schedule.dayOfMonth = [today.getDate()];
            payload.calendarInfo.schedule.month = [today.getMonth()];
            payload.calendarInfo.schedule.year = [today.getFullYear()];

            const request = { data: { ...payload } };
            const response = await this.update(request, true);
            this.addWorkOrderTypesToWorkOrder(response, userNewPayload);
        },
        reload() {
            this.cleanData();
            this.loadData();
        },
        refreshCalendar() {
            this.calendar.refreshRows();
            this.calendar.refreshColumns();
        },
        getRequestObject(newUserInput, currentInput, options = { isEdit: false, skipCreateRequest: false }) {
            /* eslint no-param-reassign: "error" */
            // assign priority based on the index
            if (!options.isEdit) {
                currentInput.priority = this.prioritiesValues[currentInput.priority];
            }

            const { data } = newUserInput;
            let request = {};
            if (!options.skipCreateRequest) {
                request = {
                    team: this.teamID,
                    jobName: data.title,
                    calendarInfo: {
                        forecolor: data.forecolor,
                        color: data.color,
                        schedule: options.isEdit ? newUserInput.schedule.toInput(true) : newUserInput.schedule,
                    },
                };
                this.populateDataForMobile(newUserInput, request.calendarInfo);
            }

            if (options.isEdit) {
                // delete some properties to avoid overwrite when doing Object.assign.
                if (!options.skipCreateRequest) {
                    delete data.jobName;
                    delete data.calendarInfo;
                }

                data.priority = data.priority?.value ? data.priority.value : this.prioritiesValues[data.priority];
                if (data.assignedTo?.length) {
                    // TODO check it after this task AQ-7081
                    if (data.assignedTo[0].id) {
                        const [assignedTo] = data.assignedTo;
                        data.assignedTo = [assignedTo.id];
                    }
                }
                // user did not select a new site.
                if (typeof data.site !== 'string') {
                    data.site = data.site?.id ? data.site.id : null;
                }
                // user did not select a new workOrderType.
                if (data.workOrderType && typeof data.workOrderType !== 'string') {
                    data.workOrderType = data.workOrderType.id;
                }
            }
            request = Object.assign(request, currentInput || data);
            return request;
        },
        setCalendarLanguage() {
            this.$dayspan.setLocale(this.lang, true);
        },
        async calendarStateChanged(state) {
            await this.$rq.updateCalendarView(this.member.id, state);
        },
        cleanData() {
            this.appointmentInfos = {
                po: '',
                so: '',
                workOrderType: null,
                reportType: null,
                billable: false,
                priority: 1,
                description: '',
                site: null,
                limitDate: null,
                limiteForCompletingTheJob: null,
                menu: null,
                time: '',
                assignedTo: [],
            };
        },

        getCalendarTime(calendarEvent) {
            const sa = calendarEvent.start.format('a');
            const ea = calendarEvent.end.format('a');
            let sh = calendarEvent.start.format('h');
            let eh = calendarEvent.end.format('h');
            if (calendarEvent.start.minute !== 0) {
                sh += calendarEvent.start.format(':mm');
            }
            if (calendarEvent.end.minute !== 0) {
                eh += calendarEvent.end.format(':mm');
            }
            return (sa === ea) ? (`${sh} - ${eh}${ea}`) : (`${sh + sa} - ${eh}${ea}`);
        },
        formatDate(date) {
            if (date) {
                const newDate = new Date(date);
                return `${newDate.toISOString().substr(0, 10)} ${newDate.toISOString().substr(11, 5)}`;
            }
            return '';
        },
        async getMembers() {
            const client = this.site?.client;
            const queryParams = this.hasRole(roles.ADMINISTRATOR.value) ? {} : roles.ADMINISTRATOR.value;
            let response;
            if (client) {
                response = await this.$rq.getClientMembers(client, { ...queryParams, pagination: false });
                if (response && response.data) {
                    const { data } = response;
                    if (this.member?.id && !data?.members?.find(member => member.id === this.member.id)) {
                        this.members = [this.member, ...data.members];
                    } else {
                        this.members = data.members;
                    }
                }
            }
        },
    },

};
</script>

<style lang="scss" scoped>
    .v-cloak {
        display: none;
    };
</style>

<style lang="scss">
@media screen and (max-width: 960px) {
    .ds-week-date {
        font-size: 1rem !important
  }
}

  .ds-fullscreen > *  {
    max-height: 300px
  }
</style>
